import { useCallback, useMemo } from 'react';

import { useActivateUser, useDeactivateUser, useHoldUser } from '@/api/generated';
import { GeneralStatusEnum } from '@/components/statuses/GeneralStatus/types';

import { useUserPermissions } from './permissions/useUserPermissions';

const USER_STATUS_TRANSITION_ACTIONS = {
  ACTIVATE: 'ACTIVATE',
  DEACTIVATE: 'DEACTIVATE',
  HOLD: 'HOLD',
  RESUME: 'RESUME'
};

interface UseUserStatusActionsOptions {
  userId: string;
  currentStatus?: string;
  onChange: (newStatus: string) => void;
}

export const useUserStatusActions = (options: UseUserStatusActionsOptions) => {
  const { userId, currentStatus, onChange } = options;
  const { value: { canUpdate: canUpdateUser } = {} } = useUserPermissions({
    variables: { userId }
  });

  const { mutateAsync: activateUser, isLoading: isActivateLoading } = useActivateUser();
  const { mutateAsync: deactivateUser, isLoading: isDeactivateLoading } = useDeactivateUser();
  const { mutateAsync: holdUser, isLoading: isHoldLoading } = useHoldUser();

  const isStatusLoading = isActivateLoading || isDeactivateLoading || isHoldLoading;
  const mutationOptions = useMemo(() => ({ userId }), [userId]);

  const statusTransitions = useMemo(
    () => [
      {
        from: GeneralStatusEnum.INACTIVE,
        to: GeneralStatusEnum.ACTIVE,
        name: USER_STATUS_TRANSITION_ACTIONS.ACTIVATE,
        handler: () => activateUser(mutationOptions)
      },
      {
        from: GeneralStatusEnum.ACTIVE,
        to: GeneralStatusEnum.INACTIVE,
        name: USER_STATUS_TRANSITION_ACTIONS.DEACTIVATE,
        handler: () => deactivateUser(mutationOptions)
      },
      {
        from: GeneralStatusEnum.ACTIVE,
        to: GeneralStatusEnum.ON_HOLD,
        name: USER_STATUS_TRANSITION_ACTIONS.HOLD,
        handler: () => holdUser(mutationOptions)
      },
      {
        from: GeneralStatusEnum.ON_HOLD,
        to: GeneralStatusEnum.ACTIVE,
        name: USER_STATUS_TRANSITION_ACTIONS.RESUME,
        handler: () => activateUser(mutationOptions)
      }
    ],
    [activateUser, deactivateUser, holdUser, mutationOptions]
  );

  const availableTransitions = useMemo(
    () => (canUpdateUser ? statusTransitions.filter(({ from }) => from === currentStatus) : []),
    [canUpdateUser, currentStatus, statusTransitions]
  );
  const availableStatuses = useMemo(
    () => [currentStatus, ...availableTransitions.map(({ to }) => to)],
    [availableTransitions, currentStatus]
  );
  const isFinalState = useMemo(() => !availableTransitions.length, [availableTransitions]);

  const availableActionsMap = useMemo(
    () => new Map(availableTransitions.map(({ name }) => [name, true])),
    [availableTransitions]
  );

  const isActivateAvailable = useMemo(
    () => availableActionsMap.has(USER_STATUS_TRANSITION_ACTIONS.ACTIVATE),
    [availableActionsMap]
  );
  const isDeactivateAvailable = useMemo(
    () => availableActionsMap.has(USER_STATUS_TRANSITION_ACTIONS.DEACTIVATE),
    [availableActionsMap]
  );
  const isHoldAvailable = useMemo(
    () => availableActionsMap.has(USER_STATUS_TRANSITION_ACTIONS.HOLD),
    [availableActionsMap]
  );
  const isResumeAvailable = useMemo(
    () => availableActionsMap.has(USER_STATUS_TRANSITION_ACTIONS.RESUME),
    [availableActionsMap]
  );

  const handleStatusChange = useCallback(
    async (newStatus: string) => {
      const originalStatus = currentStatus;
      try {
        const transition = availableTransitions.find(({ from, to }) => originalStatus === from && newStatus === to);
        if (!transition) {
          throw new Error(`Transition impossible: ${originalStatus} => ${newStatus}`);
        }

        onChange(newStatus);
        if (transition.handler) {
          await transition.handler();
        } else {
          // eslint-disable-next-line no-console
          console.warn(`Transition is not implemented: ${transition.name}`);
        }
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
        onChange(originalStatus || '');
      }
    },
    [availableTransitions, currentStatus, onChange]
  );

  const handleActivate = useCallback(() => handleStatusChange(GeneralStatusEnum.ACTIVE), [handleStatusChange]);

  const handleDeactivate = useCallback(() => handleStatusChange(GeneralStatusEnum.INACTIVE), [handleStatusChange]);

  const handleHold = useCallback(() => handleStatusChange(GeneralStatusEnum.ON_HOLD), [handleStatusChange]);

  const handleResume = useCallback(() => handleStatusChange(GeneralStatusEnum.ACTIVE), [handleStatusChange]);

  return {
    isLoading: isStatusLoading,
    isFinalState,
    availableStatuses,
    activate: handleActivate,
    deactivate: handleDeactivate,
    hold: handleHold,
    resume: handleResume,
    change: handleStatusChange,
    isActivateAvailable,
    isDeactivateAvailable,
    isHoldAvailable,
    isResumeAvailable
  };
};
